import { useCallback, useEffect, useRef, useState } from 'react'
import type { PlayerInputRouter } from '../app/input-family'
import type { ControlInput, Vec2 } from '../game/types'
import { EMPTY_INPUT } from '../game/vehicle'
import { mapJoystick } from './mobile-control-model'

const JOYSTICK_RADIUS = 80
type TouchAction = 'fire' | 'drift' | 'boost'

interface MobileControlsProps {
  router: PlayerInputRouter
  onPause: () => void
  onCamera: () => void
}

const NEUTRAL_ACTIONS: Record<TouchAction, boolean> = { fire: false, drift: false, boost: false }

function vibrate(): void {
  try {
    navigator.vibrate?.(8)
  } catch {
    // Haptics are an optional enhancement and can be blocked by the browser.
  }
}

export function MobileControls({ router, onPause, onCamera }: MobileControlsProps) {
  const snapshot = useRef<ControlInput>({ ...EMPTY_INPUT })
  const joystickPointer = useRef<number | null>(null)
  const joystickOrigin = useRef<Vec2>({ x: 0, y: 0 })
  const actionPointers = useRef<Record<TouchAction, number | null>>({
    fire: null,
    drift: null,
    boost: null,
  })
  const [knob, setKnob] = useState<Vec2>({ x: 0, y: 0 })
  const [actions, setActions] = useState(NEUTRAL_ACTIONS)

  const publish = useCallback(
    (patch: Partial<ControlInput>) => {
      snapshot.current = { ...snapshot.current, ...patch }
      router.update('touch', snapshot.current)
    },
    [router],
  )

  const clearVisualInput = useCallback(() => {
    joystickPointer.current = null
    actionPointers.current = { fire: null, drift: null, boost: null }
    snapshot.current = { ...EMPTY_INPUT }
    setKnob({ x: 0, y: 0 })
    setActions(NEUTRAL_ACTIONS)
    router.clear('touch')
  }, [router])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') clearVisualInput()
    }
    window.addEventListener('blur', clearVisualInput)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', clearVisualInput)
      document.removeEventListener('visibilitychange', onVisibility)
      joystickPointer.current = null
      actionPointers.current = { fire: null, drift: null, boost: null }
      snapshot.current = { ...EMPTY_INPUT }
      router.clear('touch')
    }
  }, [clearVisualInput, router])

  const startJoystick = (event: React.PointerEvent<HTMLFieldSetElement>) => {
    if (joystickPointer.current !== null) return
    event.preventDefault()
    joystickPointer.current = event.pointerId
    joystickOrigin.current = { x: event.clientX, y: event.clientY }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    vibrate()
  }

  const moveJoystick = (event: React.PointerEvent<HTMLFieldSetElement>) => {
    if (joystickPointer.current !== event.pointerId) return
    event.preventDefault()
    const rawOffset = {
      x: event.clientX - joystickOrigin.current.x,
      y: event.clientY - joystickOrigin.current.y,
    }
    const magnitude = Math.hypot(rawOffset.x, rawOffset.y)
    const visualScale = magnitude > JOYSTICK_RADIUS ? JOYSTICK_RADIUS / magnitude : 1
    setKnob({ x: rawOffset.x * visualScale, y: rawOffset.y * visualScale })
    publish(mapJoystick(rawOffset, JOYSTICK_RADIUS))
  }

  const releaseJoystick = (event: React.PointerEvent<HTMLFieldSetElement>) => {
    if (joystickPointer.current !== event.pointerId) return
    joystickPointer.current = null
    setKnob({ x: 0, y: 0 })
    publish({ throttle: 0, brake: 0, steer: 0 })
  }

  const startAction = (action: TouchAction, event: React.PointerEvent<HTMLButtonElement>) => {
    if (actionPointers.current[action] !== null) return
    event.preventDefault()
    actionPointers.current[action] = event.pointerId
    event.currentTarget.setPointerCapture?.(event.pointerId)
    setActions((current) => ({ ...current, [action]: true }))
    publish({ [action]: true })
    vibrate()
  }

  const releaseAction = (action: TouchAction, event: React.PointerEvent<HTMLButtonElement>) => {
    if (actionPointers.current[action] !== event.pointerId) return
    actionPointers.current[action] = null
    setActions((current) => ({ ...current, [action]: false }))
    publish({ [action]: false })
  }

  return (
    <section className="mobile-controls" aria-label="Touch race controls" data-testid="mobile-controls">
      <button className="mobile-system-button mobile-system-button--pause" type="button" onClick={onPause}>
        <span aria-hidden="true">Ⅱ</span>
        <span className="sr-only">Pause race</span>
      </button>
      <button className="mobile-system-button mobile-system-button--camera" type="button" onClick={onCamera}>
        <span aria-hidden="true">CAM</span>
        <span className="sr-only">Switch camera</span>
      </button>

      <fieldset
        className="mobile-joystick"
        onPointerDown={startJoystick}
        onPointerMove={moveJoystick}
        onPointerUp={releaseJoystick}
        onPointerCancel={releaseJoystick}
      >
        <legend className="sr-only">Drive joystick</legend>
        <span className="mobile-joystick__ticks" aria-hidden="true" />
        <span
          className="mobile-joystick__knob"
          aria-hidden="true"
          style={{ transform: `translate3d(${knob.x}px, ${knob.y}px, 0)` }}
        />
        <span className="mobile-joystick__label" aria-hidden="true">
          DRIVE
        </span>
      </fieldset>

      <fieldset className="mobile-actions">
        <legend className="sr-only">Combat and drift controls</legend>
        {(['drift', 'boost', 'fire'] as const).map((action) => (
          <button
            key={action}
            className={`mobile-action mobile-action--${action}`}
            type="button"
            aria-label={action[0]?.toUpperCase() + action.slice(1)}
            aria-pressed={actions[action]}
            data-active={actions[action] || undefined}
            onPointerDown={(event) => startAction(action, event)}
            onPointerUp={(event) => releaseAction(action, event)}
            onPointerCancel={(event) => releaseAction(action, event)}
          >
            <span>{action.toUpperCase()}</span>
            <small>{action === 'fire' ? 'R1' : action === 'drift' ? 'HOLD' : 'N₂O'}</small>
          </button>
        ))}
      </fieldset>
    </section>
  )
}
